import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormOf } from '@devinco/ui/legacy';

import { FormValueType, MergeableTrip, TripDetailsForm } from './interfaces';

@Injectable()
export class TripRangeSelectionService {
  // reconcile the merge-trip form to a single contiguous range that always
  // contains the default-selected (anchor) trip; returns the resulting
  // selected trip ids (anchor included)
  applyRangeSelection(
    mergeTripForm: FormGroup<FormOf<FormValueType>>,
    allTrips: MergeableTrip[],
    currentFormVal: FormValueType,
    prevFormVal: FormValueType | undefined,
    defaultSelectedTripId: number | undefined
  ): number[] {
    const allTripIds = allTrips.map(t => Number(t.TripID));
    const allTripKeys = allTripIds.map(String);
    const anchorIndex =
      defaultSelectedTripId !== undefined
        ? allTripIds.indexOf(Number(defaultSelectedTripId))
        : -1;

    // the user-clicked trip is the one whose value changed since the last emit
    const changedKey = allTripKeys.find(
      key => currentFormVal[key] !== prevFormVal?.[key]
    );

    let minIndex: number;
    let maxIndex: number;
    if (!changedKey) {
      minIndex = anchorIndex;
      maxIndex = anchorIndex;
    } else {
      const changedIndex = allTripKeys.indexOf(changedKey);
      const wasSelected = currentFormVal[changedKey] === true;
      if (wasSelected) {
        // extend bounds to span the click, the anchor, and any still-checked trip
        minIndex =
          anchorIndex >= 0 ? Math.min(anchorIndex, changedIndex) : changedIndex;
        maxIndex =
          anchorIndex >= 0 ? Math.max(anchorIndex, changedIndex) : changedIndex;
        allTripKeys.forEach((key, i) => {
          if (currentFormVal[key] === true) {
            if (i < minIndex) minIndex = i;
            if (i > maxIndex) maxIndex = i;
          }
        });
      } else if (anchorIndex < 0) {
        // no anchor: deselect everything
        minIndex = -1;
        maxIndex = -1;
      } else if (changedIndex < anchorIndex) {
        // shrink the side below the anchor: new lower bound is one past the click
        minIndex = changedIndex + 1;
        maxIndex = anchorIndex;
        allTripKeys.forEach((key, i) => {
          if (currentFormVal[key] === true && i > maxIndex) maxIndex = i;
        });
      } else {
        // shrink the side above the anchor: new upper bound is one before the click
        maxIndex = changedIndex - 1;
        minIndex = anchorIndex;
        allTripKeys.forEach((key, i) => {
          if (currentFormVal[key] === true && i < minIndex) minIndex = i;
        });
      }
    }

    const selectedIds: number[] = [];
    allTripKeys.forEach((key, i) => {
      const ctl = mergeTripForm.controls[key];
      const desired = minIndex >= 0 && i >= minIndex && i <= maxIndex;
      if (desired) {
        selectedIds.push(allTripIds[i]);
        if (!ctl.disabled && ctl.value !== true) {
          ctl.setValue(true, { emitEvent: false });
        }
      } else if (!ctl.disabled && ctl.value === true) {
        ctl.setValue(false, { emitEvent: false });
      }
    });

    return selectedIds;
  }

  controlPurposeValueAndItems(
    selectedTripIds: number[],
    tripDetailsForm: FormGroup<FormOf<TripDetailsForm>>,
    allTrips: MergeableTrip[]
  ): { purpose: string }[] {
    const selectedTripsPurposes = selectedTripIds.map(id => {
      return allTrips.find(trip => trip.TripID === id)?.Purpose;
    });
    // get non repeating purposes from selected trips
    const availablePurposes = [...new Set(selectedTripsPurposes)].filter(
      p => !!p
    ) as string[];
    const purposeControl = tripDetailsForm.controls.purpose;

    if (!availablePurposes.length) {
      purposeControl?.setValue([]);
      return [];
    }

    const purposesIntersection = availablePurposes.filter(value =>
      (purposeControl?.value || []).includes(value)
    );
    purposeControl?.setValue(purposesIntersection);
    return availablePurposes.map(item => ({ purpose: item }));
  }

  toggleMergedTripFields(
    isTripMerged: boolean,
    mergeTripForm: FormGroup<FormOf<FormValueType>>,
    tripDetailsForm: FormGroup<FormOf<TripDetailsForm>>
  ): void {
    if (isTripMerged) {
      mergeTripForm.disable({ emitEvent: true });
      tripDetailsForm.disable();
    } else {
      mergeTripForm.enable();
      tripDetailsForm.enable();
    }
  }
}
